import React, { useEffect, useState } from "react";
import gql from "graphql-tag";
import { useCookies } from "react-cookie";
import { LocationContext } from "@reach/router";
import styled from "@emotion/styled";
import { FadeIn } from "@utils";
import { useToggle, useStoreAppValue } from "@hooks";
import { Box } from "@atoms";
import { renderIf } from "@services";
import { LinkModalTerms } from "../LinkModalTerms";
import { LinkModalPromo } from "../LinkModalPromo";
import { LinkModalHero } from "../LinkModalHero";
import { LinkModalPostShopQuery } from "../LinkModalPostShop";
import { LinkModalFundraiserListQuery } from "../LinkModalFundraiserList/LinkModalFundraiserListQuery";
import { InStoreModalQuery } from "../../inStoreComponents";
import { LinkModalGiftcardContainer } from "../linkModalGiftCardComponents";
import { LinkModalFragment } from "./__generated__/LinkModalFragment";
import { LinkModalCampaignFragment } from "./__generated__/LinkModalCampaignFragment";
import { LinkModalBorder } from "../LinkModalBorder";

// == Types ================================================================

interface IProps {
  link: LinkModalFragment;
  locationContext: LocationContext;
  campaign?: LinkModalCampaignFragment | null;
  campaignId?: string;
  locationId?: string;
}

// == Constants ============================================================

LinkModal.fragments = {
  link: gql`
    fragment LinkModalFragment on Link {
      id
      type
      defaultBrand {
        id
      }
      ...LinkModalHeroFragment
      ...LinkModalTermsFragment
      ...LinkModalPromoFragment
      ...LinkModalPostShopQueryFragment
      ...LinkModalGiftcardContainerFragment
    }
    ${LinkModalHero.fragment}
    ${LinkModalTerms.fragments.fields}
    ${LinkModalPromo.fragments.fields}
    ${LinkModalPostShopQuery.fragment}
    ${LinkModalGiftcardContainer.fragment}
  `,
  campaign: gql`
    fragment LinkModalCampaignFragment on Campaign {
      id
      viewerFundraiser @include(if: $hasUser) {
        id
        userId
      }
    }
  `,
};

// == Component ============================================================

export default function LinkModal({
  locationId,
  locationContext,
  campaign,
  campaignId,
  link,
}: IProps) {
  const {
    featureFlags: { newCollection, newCheckout },
    isMember,
  } = useStoreAppValue();
  const [fundraiserId, setFundraiserId] = useState(campaign?.viewerFundraiser?.id);

  const [showPostShopModal, togglePostShopModal] = useToggle(false);

  const [, setCookie] = useCookies([]);
  const { defaultBrand } = link;
  const { brandId } = locationContext.location.state;
  useEffect(() => {
    setCookie("brand_id", brandId || defaultBrand.id, { path: "/" });
  }, [brandId, defaultBrand.id, setCookie]);

  if (newCollection && link.type === "EMPYR") {
    return <InStoreModalQuery campaignId={campaignId} linkId={link.id} locationId={locationId} />;
  }

  // link modal
  if (!showPostShopModal) {
    return (
      <FadeIn>
        <ELinkModalContainer>
          <LinkModalHero
            campaignId={campaignId}
            fundraiserId={fundraiserId}
            link={link}
            locationContext={locationContext}
            onLinkClick={togglePostShopModal}
          >
            {renderIf(!!campaignId && !isMember)(
              <LinkModalFundraiserListQuery onFundraiserSelect={setFundraiserId} />
            )}
          </LinkModalHero>
          <LinkModalBorder />
          {renderIf(newCheckout && link.type === "GIFTCARDOFFER")(
            <>
              <LinkModalGiftcardContainer
                campaignId={campaignId}
                fundraiserId={fundraiserId}
                link={link}
                locationContext={locationContext}
                onLinkClick={togglePostShopModal}
              />
              <LinkModalBorder />
            </>
          )}
          {!!link.promoTitle && !!link.promoDescription && (
            <>
              <LinkModalPromo link={link} p={3} />
              <LinkModalBorder />
            </>
          )}
          <LinkModalTerms link={link} p={3} />
        </ELinkModalContainer>
      </FadeIn>
    );
  }

  // post shop modal
  return <LinkModalPostShopQuery link={link} />;
}

// == Styles ===============================================================

export const ELinkModalContainer = styled(Box)`
  height: 100vh;
  overflow-y: scroll;
  padding-bottom: 90px;
  @media only screen and (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    height: 80vh;
  }
`;
